import React, { useEffect, useState } from "react";
import process_vid from '../../assets/process_vid.mp4';
import { motion } from "framer-motion";

const steps = [
  { id: 1, label: "Fabric", duration: 14000 },
  { id: 2, label: "Style", duration: 19000 },
  { id: 3, label: "Embroidery", duration: 11000 },
  { id: 4, label: "Measurement", duration: 6000 },
];

export default function Process() {
  const [active, setActive] = useState(0);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    let timer;

    const run = () => {
      timer = setTimeout(() => {
        setActive((prev) => {
            if (prev === steps.length - 1) {
                setCycle(c => c + 1);
                return 0;
            }
            return prev + 1;
        });
      }, steps[active].duration);
    };

    run();
    return () => clearTimeout(timer);
  }, [active]);

  return (
    <section className="w-full bg-[#f5f5f5] py-12 lg:py-20 overflow-hidden">
      <div className="w-full max-w-full px-[10px] xl:px-[70px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-20 items-start">
        
        {/* Block 1: Heading & Steps (Mobile: Order 1, Desktop: Col 1 Row 1) */}
        <div className="flex flex-col justify-center order-1 lg:order-none lg:col-start-1 lg:row-start-1">
            <h2 className="text-center lg:text-left font-heading text-[clamp(2rem,4vw,6rem)] font-semibold leading-tight text-[#14213D] mb-8 lg:mb-12">
                How It Works
            </h2>

            {/* Steps Indicator */}
            <div className="grid grid-cols-4 w-full max-w-lg xl:max-w-[40vw] mx-auto lg:mx-0 relative mb-8 lg:mb-12">
                {steps.map((step, index) => {
                    const isActive = index === active;
                    const isCompleted = index < active;
                    
                    return (
                        <div
                            key={step.id}
                            className="flex flex-col items-center relative z-10 cursor-pointer"
                            onClick={() => {
                                setActive(index);
                                // If user manually clicks, we should probably reset cycle or handle it? 
                                // For now just let it run.
                            }}
                        >
                            <div
                                className={`w-[clamp(3.5rem,4vw,7rem)] h-[clamp(3.5rem,4vw,7rem)] rounded-full flex items-center justify-center font-bold text-[clamp(1.125rem,1.5vw,2.5rem)] transition-all duration-500 shadow-lg inter
                                ${isActive || isCompleted
                                    ? "bg-[#D4AF37] text-black scale-110 shadow-[#D4AF37]/40" 
                                    : "bg-gray-200 text-gray-500"}`}
                            >
                                0{step.id}
                            </div>

                            <p
                                className={`mt-4 text-[clamp(0.5rem,1vw,1.5rem)] uppercase tracking-normal sm:tracking-[0.2em] font-medium transition-colors duration-300 inter truncate w-full text-center
                                ${isActive || isCompleted ? "text-black" : "text-gray-400"}`}
                            >
                                {step.label}
                            </p>

                            {/* Connecting Line */}
                            {index < steps.length - 1 && (
                                <div className="absolute top-7 left-1/2 w-full h-[2px] bg-gray-200 -z-10">
                                    <motion.div 
                                        key={`${cycle}-${index}`}
                                        initial={{ width: isCompleted ? "100%" : "0%" }}
                                        animate={{ width: isCompleted ? "100%" : (isActive ? "100%" : "0%") }}
                                        transition={{ 
                                            duration: isActive ? steps[index].duration / 1000 : 0, 
                                            ease: "linear" 
                                        }}
                                        className="h-full bg-[#D4AF37]"
                                    />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>

        {/* Block 2: Details (Mobile: Order 3, Desktop: Col 1 Row 2) */}
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-100 min-h-[clamp(250px,25vw,600px)] flex flex-col justify-center transition-all duration-500 order-3 lg:order-none lg:col-start-1 lg:row-start-2">
            <motion.div
                key={active}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {active === 0 && (
                    <div>
                        <h3 className="font-heading text-[clamp(1.5rem,2.5vw,4rem)] text-[#14213D] mb-4 cg">
                            Select the Fabric
                        </h3>
                        <p className="font-body text-gray-600 text-[clamp(1rem,1.2vw,2rem)] leading-relaxed inter">
                            Choose from an extensive collection of premium-quality fabrics, meticulously selected by experts to ensure the perfect match for your chosen design, giving you a blend of style, comfort, and elegance.
                        </p>
                    </div>
                )}

                {active === 1 && (
                    <div>
                        <h3 className="font-heading text-[clamp(1.5rem,2.5vw,4rem)] text-[#14213D] mb-4 cg">
                            Design Your Style
                        </h3>
                        <p className="font-body text-gray-600 text-[clamp(1rem,1.2vw,2rem)] leading-relaxed inter">
                            Explore a diverse range of stunning styles, thoughtfully curated by experts to suit every taste and occasion. Discover the perfect match to complement your chosen piece effortlessly.
                        </p>
                    </div>
                )}

                {active === 2 && (
                    <div>
                        <h3 className="font-heading text-[clamp(1.5rem,2.5vw,4rem)] text-[#14213D] mb-4 cg">
                            Select the Embroidery
                        </h3>
                        <p className="font-body text-gray-600 text-[clamp(1rem,1.2vw,2rem)] leading-relaxed inter">
                            Add a signature touch with refined embroidery options, crafted to elevate your garment with subtle detailing or bold personal expression.
                        </p>
                    </div>
                )}

                {active === 3 && (
                    <div>
                        <h3 className="font-heading text-[clamp(1.5rem,2.5vw,4rem)] text-[#14213D] mb-4 cg">
                            Give Your Measurements
                        </h3>
                        <p className="font-body text-gray-600 text-[clamp(1rem,1.2vw,2rem)] leading-relaxed inter">
                            Get the perfect fit with ease. Follow our quick measurement guide, choose from our size chart, or send us your favorite outfit to replicate its measurements flawlessly.
                        </p>
                    </div>
                )}
            </motion.div>
        </div>

        {/* Block 3: Video (Mobile: Order 2, Desktop: Col 2 Row 1-2) */}
        <div className="relative w-full flex flex-col items-center order-2 lg:order-none lg:col-start-2 lg:row-start-1 lg:row-span-2 h-full">
            <div className="relative w-full rounded-2xl overflow-hidden border border-gray-100 shadow-[0_20px_50px_rgba(0,0,0,0.05)] bg-white aspect-video lg:aspect-auto lg:h-full p-2">
                <video 
                    className='w-full h-full object-cover rounded-xl' 
                    autoPlay 
                    muted 
                    playsInline 
                    loop
                >
                    <source src={process_vid} /> 
                </video>
            </div>
        </div>

      </div>
      <style>{`
        .cg { font-family: 'Cormorant Garamond', serif; }
        .inter { font-family: 'Inter', sans-serif; }
      `}</style>
    </section>
  );
}
